/**
 * Courier Quote Controller
 *
 * Powers the live delivery-quote panel on the
 * customer-facing delivery checkout form. While the
 * customer fills out their delivery address, this
 * controller debounces a POST to the quote endpoint
 * and renders the returned fee + ETA back into the
 * page. The hidden `courier_quote_token` field carries
 * the server's signed account of what it priced, which
 * is what lets the create honor the quoted fee at
 * submit; `courier_quote_id` and
 * `courier_quote_expires_at` are kept in sync for this
 * controller's own use: the submit button and the
 * re-quote timer. An auto re-quote is scheduled two
 * minutes before expiry while the form is open.
 *
 * Provider-neutral: the endpoint prices the trip
 * through whichever courier network the venue runs
 * on, so nothing here knows or cares which one
 * answered.
 *
 * The controller also enables/disables the form's
 * submit button based on quote presence. The
 * customer cannot submit the form until a valid
 * quote is in hand.
 *
 * Address fields are NOT Stimulus targets. They
 * live inside the shared `AddressFormComponent`,
 * which is reused by non-courier flows. Instead,
 * this controller queries the form by predictable
 * Rails input names (e.g.
 * `restaurant_orders_delivery_create_form` \
 * `[delivery_street_address]`) on `connect()` and
 * binds `input` listeners imperatively. The form
 * scope is supplied via the `formScope` value so
 * the same controller can be reused if the form
 * scope ever changes.
 */
import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";
import { post } from "@rails/request.js";
import * as Sentry from "@sentry/browser";
import {
  setSubmitBlock,
  setSubmitPrompt,
  submitButtonsFor,
} from "../../shared/submit_blocks";

const REQUOTE_LEAD_MS = 2 * 60 * 1000;

// The floor under every re-quote timer.
//
// `scheduleRequote` works off `expires_at`, an absolute
// server timestamp, against the DEVICE clock, and it
// cannot do otherwise: on connect that expiry comes out of
// a hidden field the server rendered, which may be minutes
// old by the time Turbo restores the page, so a duration
// sent beside it would already be wrong. A phone whose
// clock runs 13 minutes fast therefore reads every fresh
// quote as already inside the lead above, and each
// re-quote then scheduled the next one at zero: an idle
// tab POSTing at the debounce floor, about 100 requests a
// minute, every one of them a billed geocode plus up to
// two provider round trips (CAL-476).
//
// 30s turns that into 2 requests a minute, and it is well
// inside `REQUOTE_LEAD_MS`, so a quote genuinely in its
// last two minutes is still refreshed with at least 90s of
// validity to spare. It floors EVERY delay rather than
// only the non-positive ones, because a 40ms delay loops
// exactly as hard as a zero.
const MIN_REQUOTE_DELAY_MS = 30 * 1000;

const DEBOUNCE_MS = 600;

// How long we wait for the quote endpoint before calling
// it dead.
//
// A `fetch` whose connection goes away does not reject;
// the promise simply never settles. Food is ordered from
// phones on mobile data, so this is not an exotic case.
// Without a deadline the checkout sits on "Calculating
// delivery…" forever: `showLoading()` has put "Try
// again" away for the flight, no quote lands,
// `quoteBlocked` is never released, and the only way out
// is a reload (CAL-464).
//
// The number is set from the SERVER's own ceiling rather
// than from what feels tolerable, because killing a
// request the server is still working on manufactures a
// failure and costs the provider call it interrupts.
// This endpoint makes up to two sequential
// `Listo::Client` calls (the quote, then
// `FetchStoreBalanceService` to decide the cash rail),
// and that client allows 10s to connect plus 30s to read
// EACH. Nothing shortens it: there is no rack-timeout in
// the app and no proxy cap in front of it. So the honest
// worst case is ~80s of provider time plus Rails' own
// work, and 90s clears it.
//
// It is deliberately a long wait. The alternative is not
// a shorter spinner, it is a customer told their address
// failed when it had not.
const REQUEST_TIMEOUT_MS = 90 * 1000;

const ADDRESS_KEYS = [
  "street_address",
  // Priced as part of the drop-off so the quote and
  // the create describe the same address: the courier
  // network binds a create to the quote it came from
  // and treats an `apt_unit` that appears on only one
  // of them as drift.
  "interior",
  "city",
  "state",
  "country",
  "postal_code",
  "suburb",
];

const REQUIRED_KEYS = [
  "street_address",
  "city",
  "country",
];

// The point the customer confirmed: Google's own pin
// for the place they picked, or wherever they dragged
// it to. Deliberately NOT part of ADDRESS_KEYS: these
// two are written by `delivery-pin` into the map card,
// which renders OUTSIDE this controller's element, and
// they are read as a PAIR because half a pin is not a
// position.
const PIN_KEYS = ["latitude", "longitude"];

export default class extends Controller {
  static targets = [
    "feeDisplay",
    "etaDisplay",
    "errorDisplay",
    "loading",
    "panel",
    "quoteId",
    "quoteExpiresAt",
    "quoteFeeCents",
    "quoteFeeCurrency",
    // The terms the quote in hand was priced under.
    // Payment method is an INPUT to the price on a
    // courier network, so a cash quote and a prepaid
    // quote to the same door are two different quotes
    // and the server must be able to tell which one it
    // was handed. The customer share is snapshotted
    // alongside because the at-door cap is charged
    // against `order value + customer fee share`.
    "paymentMethod",
    "quoteCustomerFeeCents",
    // The quote, signed by the endpoint that priced it.
    // Every other hidden field here is ours to draw the
    // panel with; this one is the server's, carried
    // back untouched, and it is the only one the create
    // believes. It also names the point the quote was
    // priced against, so a pin that has not moved does
    // not buy a second provider call at submit.
    "quoteToken",
    // Optional: only the providers that commit a
    // platform surcharge render the field, and only
    // those return `surcharge_cents`. Every write is
    // guarded by `hasCommittedSurchargeCentsTarget`.
    "committedSurchargeCents",
    "submit",
    "autoDispatchCallout",
    "autoDispatchEta",
    "quoteSummary",
    // The hint line under the address row: what is
    // still missing before a fee can exist. Optional: a
    // checkout that renders no such line falls back to
    // writing the same copy into `errorDisplay`, which
    // is where it went before the line existed.
    "needsAddress",
    // The text node inside it. The line leads with a
    // clock glyph and carries either of two sentences
    // (the whole address, or just the city), so the copy
    // goes in the node beside the icon rather than over
    // it, exactly as on the failure line below. Markup
    // without it keeps whatever the server rendered.
    "hintMessage",
    // The text node inside `errorDisplay`. The failure
    // line leads with an icon now, so writing the
    // message onto the wrapper would erase it.
    "errorMessage",
    // The two ways out of a failure, in the same slot at
    // the end of that line. At most ONE is ever shown,
    // decided by the server's verdict and not by the
    // sentence: a retryable failure gets "Try again", an
    // address refusal gets "Fix address", and a refusal
    // that is neither gets nothing, because its copy
    // already names the only way forward. Both are
    // optional: markup without them still renders every
    // message.
    "retryButton",
    "fixAddressLink",
  ];

  static values = {
    url: String,
    formScope: {
      type: String,
      default:
        "restaurant_orders_delivery_create_form",
    },
    // Translated strings come from the view via
    // data-courier-quote-*-text-value. We don't
    // hardcode English defaults: JS strings should
    // not bypass server-side i18n.
    fetchingText: String,
    unavailableText: String,
    needsAddressText: String,
    // The same line for the one field of an address the
    // customer can hand back on their own. The city is
    // a `<select>` whose first option is a selectable
    // blank, and picking it leaves a street, a state and
    // a postal code with nothing to price them against.
    needsCityText: String,
    // Sentence templates carrying a `%{eta}`
    // placeholder, filled in once the provider answers
    // with a time. The alternative is assembling the
    // sentence from fragments in the view, which is a
    // msgid no translator can work with.
    etaText: String,
    autoDispatchText: String,
    // What the held CTA says, in the two states this
    // controller can put it in. Both are also rendered
    // server-side into the button. These are what let
    // it swap between them without a page load, which
    // is the only way the customer ever gets from one
    // to the other.
    submitPendingText: String,
    submitNeedsAddressText: String,
    // Never rendered server-side, unlike the two above.
    // The page arrives either with an address or
    // without one; this state is only reachable by a
    // customer taking a field back off one, so the
    // browser is the only place it can be said.
    submitNeedsCityText: String,
    // Short and FIXED, unlike the failure line above
    // it: `unavailableText` is a fallback for whatever
    // sentence the server sent, so its length is not
    // ours to know, and a button is 56px tall. The
    // provider's own words go in the detail line where
    // they can wrap; the button says the state.
    submitUnavailableText: String,
    // For the failure with no server sentence at all:
    // `fetch` threw, so nothing answered. This used to
    // fall back to `unavailableText` ("We can't deliver
    // to that address"), which is a claim about the
    // address that a dead connection gives no grounds
    // for.
    requestFailedText: String,
    // The terms to price the trip under, stated
    // OUTRIGHT by the page rather than worked out from
    // the payment radios (CAL-499).
    //
    // Absent is the default and is every customer
    // checkout: `currentPaymentMethod()` falls through
    // to reading `<scope>[payment_choice]` exactly as
    // before, so nothing about that page changes.
    //
    // It exists because a page can legitimately have no
    // `payment_choice` field to read. The admin order
    // builder derives the order's choice server-side
    // from a staff radio and posts none, so the lookup
    // answered null there and every trip priced as
    // prepaid, including one the merchant had told us
    // was cash at the door. A page that knows the
    // answer should be able to say it.
    paymentMethod: String,
  };

  /**
   * Runs exactly ONCE per controller instance, which is
   * what makes it the only safe home for the state below.
   * `connect()` does not have that guarantee: on a Turbo
   * morph it runs again on the SAME instance, and it can
   * run TWICE against a single `disconnect()`: Stimulus
   * reconnects natively when morphdom re-inserts the
   * element, and `app/javascript/entrypoints/users.js`
   * separately walks every live controller calling
   * `disconnect(); connect()` inside its morph handler.
   * Allocating any of this per connect leaked a listener,
   * a debounce layer and a timer on every morph (CAL-401).
   */
  initialize() {
    // Stable handler identities, bound once and reused
    // forever. `removeEventListener` matches on
    // (type, listener, capture), so `disconnect()` can
    // only unbind what `connect()` bound while the
    // reference stays the same object. Rebinding per
    // connect left the previous listener unreachable and
    // orphaned one per morph. The same identity rule
    // makes a repeat `addEventListener` for the same
    // type + listener + capture a no-op per the DOM spec,
    // which is what lets a double connect be harmless
    // rather than additive. Do NOT move these into
    // `connect()`.
    this.boundAddressChanged =
      this.addressChanged.bind(this);
    this.boundPaymentChoiceChanged =
      this.paymentChoiceChanged.bind(this);
    this.boundPinDragged = this.pinDragged.bind(this);

    // Kept under its own name rather than assigned back
    // onto `this.requestQuote`: shadowing the prototype
    // method with its own wrapper meant the next connect
    // debounced the debounce, each layer adding another
    // DEBOUNCE_MS and its own pending timer chain. Every
    // call site that wants debouncing calls THIS; the
    // raw `requestQuote()` stays untouched on the
    // prototype.
    this.debouncedRequestQuote = debounce(
      () => this.requestQuote(),
      DEBOUNCE_MS
    );

    this.requoteTimer = null;

    // Never reset on reconnect. This counter is the only
    // thing telling an in-flight response that a newer
    // request has superseded it; winding it back to 0 let
    // an old response match a later request's token and
    // overwrite a fresher quote.
    this.currentRequestToken = 0;
  }

  connect() {
    // Everything here must survive being run twice. See
    // `initialize()` for why. Per-connect state belongs
    // there, not here.

    // A requote timer left over from a previous connect
    // has to be CLEARED, not merely forgotten: dropping
    // the handle leaves a live timeout that still fires a
    // request and that `disconnect()` can no longer
    // reach.
    this.clearRequoteTimer();

    this.addressInputs = this.findAddressInputs();
    this.bindAddressListeners();

    this.paymentChoiceInputs =
      this.findPaymentChoiceInputs();
    this.bindPaymentChoiceListeners();

    this.bindPinListener();

    this.scheduleRequoteFromExisting();
    this.refreshSubmitState();

    // If the form was rendered with a saved
    // address pre-filled, no `input` / `change`
    // event will fire, so neither the address
    // listeners nor the requote-from-existing
    // hook will pull a fresh quote. Force one
    // ourselves when the address is already
    // complete and we don't yet have a valid
    // un-expired quote.
    this.requestQuoteIfNeededOnConnect();

    // Last, so it reads the fields as this connect
    // left them. A page with no address goes through
    // `showNeedsAddress()` above, which blanks the
    // quote. Publishing first would announce a price
    // and retract it one call later. Either order ends
    // in the same place; only this one gets there
    // without saying something untrue on the way.
    this.restoreQuotedPrice();
  }

  requestQuoteIfNeededOnConnect() {
    if (!this.collectAddress()) {
      // A morph rewrites the city `<select>` from server
      // HTML: the options survive and the SELECTED value
      // does not, so this connect can arrive between the
      // rewrite and `city-selector` re-applying it. The
      // address is there; we just cannot read all of it
      // this instant. Ask again rather than declare it
      // missing: the request is a debounce away and the
      // city will be back by then (CAL-464).
      if (this.hasStreetAddress()) {
        this.debouncedRequestQuote();
        return;
      }

      // No (or incomplete) address pre-filled
      // means the panel started life rendering
      // the "Calculating…" placeholder. Swap it
      // for the "Enter your delivery address"
      // hint right away so the customer isn't
      // stuck looking at a fake spinner.
      this.showNeedsAddress();
      return;
    }

    if (!this.holdsUsableQuote()) {
      // Said now rather than when the debounce runs out.
      // The render this connect follows may have been
      // drawn by a server that never saw the address:
      // applying a discount code redirects back to a page
      // that knows only what the customer SAVED, so it
      // arrives saying "Add your address to see delivery
      // time & fee" under an address row the customer
      // can see is filled in (CAL-522). A code changes
      // the basket the fee is priced on, so asking again
      // is right; saying nothing true while we wait is
      // not.
      this.showLoading();
      this.debouncedRequestQuote();
    }
  }

  /**
   * Has the customer given us an address line?
   *
   * The line between "no address" and "an address this
   * page cannot read every field of RIGHT NOW", and the
   * reason it can be drawn on one field: of the three
   * `REQUIRED_KEYS` the country always resolves
   * (`collectAddress` defaults it), and the city is a
   * `<select>` this page populates over the network and
   * repopulates on every state change. The street is the
   * only one the customer types and nothing else writes.
   *
   * So a missing street means they have not given us an
   * address. A street with no city means one of OUR
   * fields is momentarily unreadable, and the two must
   * not end in the same place, because
   * `showNeedsAddress()` erases the failure line and the
   * "Fix address" button along with it. Fired over a
   * transient blank it takes a customer who has just
   * been told they are out of the delivery radius and
   * replaces that sentence, their only explanation and
   * their only way forward, with a request for the
   * address they can see they already gave (CAL-464).
   */
  hasStreetAddress() {
    const input =
      (this.addressInputs || {}).street_address;

    return !!input && input.value.trim() !== "";
  }

  /**
   * An address we hold but cannot currently read in
   * full. Everything the page is SAYING stays exactly as
   * it is (the failure line, its button, the hint, the
   * CTA's sentence) because none of it has been
   * contradicted by a field going blank for a round
   * trip, and this controller has nothing truer to
   * replace it with until it can ask again.
   *
   * What does not stay is the QUOTE. A price we can no
   * longer name a complete address for is not one this
   * order can be placed at, so it goes and the button
   * closes behind it. That is also what keeps the native
   * `required` validation off the customer's back: a
   * live CTA over a blank city `<select>` inside a
   * closed sheet fails submit with a browser message
   * pointing at a field they cannot see.
   *
   * Nothing is rescheduled here. The city arriving is
   * itself a `change` on an address input, which is what
   * asks for the next quote.
   *
   * And where that leaves NOTHING on the panel, the hold
   * says what it is waiting for. Silence is only ever
   * right as deference to a message already up: the
   * refusal this method exists to preserve is worth more
   * than anything it could add, and so is a spinner
   * whose answer is about to land. With the panel empty
   * there is nothing to defer TO: a held button over a
   * placeholder total, no sentence anywhere, and no
   * further event coming, because the customer's own
   * edit to the city was the last one this page will
   * see. That is a checkout with no way forward, which
   * is the defect this ticket is about rather than an
   * edge of it (CAL-464).
   *
   * The rule is what the page SAYS, never who emptied
   * the field. A blank arriving from a list reload finds
   * the refusal up and stays quiet; the customer's own
   * blank finds nothing and gets a sentence. Keyed on
   * the cause instead (whose event it was), the two
   * would have to be told apart at every future call
   * site, and getting it wrong once puts back either the
   * erased refusal or the silent hold.
   */
  holdQuote() {
    this.clearQuoteFields();
    // The fee line goes with the quote it described. It
    // is the one thing on the panel that WOULD be
    // contradicted by this: an amount still on screen
    // beside a button that has just closed, for a price
    // no longer in hand. Every other surface is left
    // alone precisely because none of them has been.
    this.clearQuoteText();
    this.hideQuoteSummary();

    // Ahead of `refreshSubmitState()` and the price on
    // purpose: `showNeedsCity()` does both, and doing
    // them twice would publish the placeholder twice.
    if (this.panelSaysNothing()) {
      this.showNeedsCity();
      return;
    }

    this.refreshSubmitState();
    this.publishPrice(null, null);
  }

  /**
   * Is there anything left under the address row
   * explaining why the button will not open?
   *
   * The three lines that can speak, all in the one slot:
   * the failure, the hint, and the spinner. The quote
   * summary is not among them: the caller has just put
   * it away, and a fee is not an explanation of its own
   * absence.
   *
   * The spinner counts because a request in flight is
   * about to say something itself: `requestQuote()`
   * returns before it bumps the staleness token in this
   * branch, so the answer it is waiting on still lands
   * and still writes. Talking over it would mean
   * replacing a sentence with another sentence a moment
   * later.
   *
   * A missing target reads as silent, which is right: a
   * checkout rendering no hint line says nothing through
   * one.
   */
  panelSaysNothing() {
    const lines = [
      this.hasErrorDisplayTarget &&
        this.errorDisplayTarget,
      this.hasNeedsAddressTarget &&
        this.needsAddressTarget,
      this.hasLoadingTarget && this.loadingTarget,
    ];

    return lines.every(
      (line) =>
        !line || line.classList.contains("hidden")
    );
  }

  /**
   * Says the price of a quote we already hold, for the
   * connect that did not land it.
   *
   * A rejected submit does not reload this checkout:
   * `entrypoints/users.js` morphs the 422 body onto the
   * live page and hands every controller a
   * `disconnect(); connect()`. The re-render is
   * `awaiting_quote?` and `checkout_total_cents == 0`,
   * both unconditional on a courier delivery, so it
   * puts the pending glyph back in every total and the
   * hold back on every button. The QUOTE survives it,
   * because it rides in ordinary hidden fields and
   * round-trips like the address does. That is why
   * `refreshSubmitState()` above frees the button on
   * the way past.
   *
   * Nothing else would say what it costs. The only
   * publisher is `applyQuote()`, no quote is going to
   * land (we hold a valid one, so nothing is
   * requested), and the result is a live "Place order"
   * over a page showing no amount anywhere: the state
   * the server-side hold exists to prevent, rebuilt one
   * layer down.
   *
   * A DOM read, not a request: the customer's share is
   * in the field the server just handed back.
   *
   * Idempotent by construction, which matters because
   * an element morphdom REPLACES gets a native connect
   * AND this pass's connect, two against one
   * disconnect. `tip-selector` recomputes its total as
   * `orderTotalCents + feeCents` from the bare cart
   * every time, so publishing twice writes the same
   * number rather than adding the fee twice.
   */
  restoreQuotedPrice() {
    if (!this.holdsUsableQuote()) return;

    const customerFeeCents = this.quotedCustomerFeeCents();

    if (customerFeeCents === null) {
      // A quote we cannot name a price for is one we
      // cannot draw the page from, whatever the id
      // says. Ask again rather than free the button
      // over a placeholder.
      this.debouncedRequestQuote();
      return;
    }

    // No formatted string to pass: the server sends one
    // with a landing quote, but the re-rendered field
    // carries only cents. `tip-selector` already
    // formats the totals it writes from the server's
    // own symbol and marks, so it formats this too.
    this.publishPrice(customerFeeCents, null);
    this.applySubmitPrompt(false);
  }

  /**
   * Is there a quote in the fields this page can still
   * be submitted at, meaning one was recorded and its
   * expiry has not passed?
   *
   * ONE reading, because the button and the price are
   * two consumers of the same fact and a page where
   * they disagree is exactly the defect above: a live
   * CTA over a total nobody wrote. A missing or
   * unparseable expiry counts as live, which is what
   * lets a provider that returns no expiry still be
   * ordered from.
   */
  holdsUsableQuote() {
    const hasQuote =
      this.hasQuoteIdTarget &&
      this.quoteIdTarget.value.trim() !== "";
    if (!hasQuote) return false;

    const expiresAt = this.hasQuoteExpiresAtTarget
      ? Date.parse(this.quoteExpiresAtTarget.value)
      : NaN;

    return isNaN(expiresAt) || expiresAt > Date.now();
  }

  /**
   * The customer's share of the fee in hand, as an
   * integer. Null when the field is absent, blank or
   * not a number. All of these mean "no price here",
   * never zero: a free delivery is a real 0 and has to
   * stay distinguishable from an empty field.
   */
  quotedCustomerFeeCents() {
    if (!this.hasQuoteCustomerFeeCentsTarget) return null;

    const raw =
      this.quoteCustomerFeeCentsTarget.value.trim();
    if (raw === "") return null;

    const cents = Number(raw);
    return Number.isFinite(cents) ? cents : null;
  }

  disconnect() {
    this.clearRequoteTimer();

    // A debounce still pending when we disconnect would
    // fire against a page that is gone, and its response
    // would run `applyQuote()`, which calls
    // `scheduleRequote()` and recreates the very timer
    // this method just cleared. Clearing the timer alone
    // is not enough; the pending call has to be dropped
    // too.
    this.debouncedRequestQuote.cancel();

    // Same hole one step later: a request already in
    // flight resolves after we are gone and applies its
    // quote to a stale page. Bumping the token (never
    // resetting it) makes the staleness check inside
    // `requestQuote()` discard that response instead.
    this.currentRequestToken++;

    this.unbindAddressListeners();
    this.unbindPaymentChoiceListeners();
    this.unbindPinListener();
  }

  /**
   * A drag moves the drop-off point, and the drop-off
   * point is what the trip is priced on. The quote on
   * screen stops describing this delivery the moment
   * the marker lands.
   *
   * Bound imperatively rather than through a
   * `data-action`, for the same reason the address and
   * payment listeners are: `entrypoints/users.js` calls
   * `disconnect(); connect()` by hand on a morph, and
   * Stimulus's own action bindings do not answer those
   * calls. A drag arriving while we are torn down would
   * schedule a request from a controller that had just
   * cancelled its work. The stable handler identity from
   * `initialize()` is what makes the repeat bind a
   * no-op, so a double connect stays harmless.
   */
  bindPinListener() {
    window.addEventListener(
      "location-map:destinationDragged",
      this.boundPinDragged
    );
  }

  unbindPinListener() {
    window.removeEventListener(
      "location-map:destinationDragged",
      this.boundPinDragged
    );
  }

  /**
   * `delivery-pin` answers the same event and writes the
   * coordinates into the hidden fields we read. Which of
   * the two listeners runs first does not matter: this
   * one only SCHEDULES, and the request that reads those
   * fields is a debounce away.
   *
   * Re-pricing here is what keeps the drag honest: the
   * customer sees the new fee next to the pin they just
   * moved, instead of meeting it as a refusal at submit.
   * The cash option re-decides for free, since its cap
   * is charged against a total the fee is part of.
   */
  pinDragged() {
    this.debouncedRequestQuote();
  }

  /**
   * The payment-choice radios live in the shared
   * layout's payment card, outside this controller's
   * element, same as the submit button. Found through
   * the form for the same reason.
   */
  findPaymentChoiceInputs() {
    const form = this.orderForm();
    if (!form) return [];

    const name =
      `${this.formScopeValue}[payment_choice]`;
    return Array.from(
      form.querySelectorAll(
        `input[type="radio"][name="${name}"]`
      )
    );
  }

  bindPaymentChoiceListeners() {
    this.paymentChoiceInputs.forEach((input) => {
      input.addEventListener(
        "change",
        this.boundPaymentChoiceChanged
      );
    });
  }

  unbindPaymentChoiceListeners() {
    if (!this.paymentChoiceInputs) return;
    this.paymentChoiceInputs.forEach((input) => {
      input.removeEventListener(
        "change",
        this.boundPaymentChoiceChanged
      );
    });
  }

  /**
   * Payment method is an input to the price, not a
   * label on it: a cash trip and a prepaid trip to the
   * same door are two different quotes. So toggling
   * asks again rather than rearranging numbers we
   * already have.
   */
  paymentChoiceChanged() {
    this.debouncedRequestQuote();
  }

  /**
   * What the customer is asking for right now. "later"
   * on a courier delivery means cash handed over at
   * the door; anything else is settled online before
   * the courier is dispatched.
   */
  currentPaymentMethod() {
    if (this.paymentMethodValue) {
      return this.paymentMethodValue;
    }

    const checked = (
      this.paymentChoiceInputs || []
    ).find((input) => input.checked);

    const value = checked
      ? checked.value
      : this.pinnedPaymentChoice();

    return value === "later" ? "cash" : "prepaid";
  }

  /**
   * A page that states the terms outright can change
   * them, and a cash trip and a prepaid trip to the
   * same door are two different quotes, so a change
   * asks again rather than rearranging numbers we
   * already have. Exactly what `paymentChoiceChanged()`
   * does for the radios below.
   *
   * Stimulus calls this once on initialization too,
   * which must NOT fire a quote: `connect()` already
   * decides whether one is needed, and a second call
   * from here would double every page's first request.
   * `lastPaymentMethodValue` is undefined until then,
   * so the first call only records the terms.
   */
  paymentMethodValueChanged(value) {
    const previous = this.lastPaymentMethodValue;
    this.lastPaymentMethodValue = value;

    if (previous === undefined) return;
    if (previous === value) return;

    this.debouncedRequestQuote();
  }

  /**
   * A checkout with only ONE usable rail renders no
   * radio group at all: `PaymentOptionComponent`
   * pins the answer with a hidden `payment_choice`
   * field instead. Reading only the radios there
   * answers "prepaid" for a cash-only courier venue
   * and prices the trip under terms the customer
   * cannot use; the create then finds the drift and
   * spends a second provider call putting it right,
   * which the customer meets as the quote-changes
   * screen for no reason they can see.
   *
   * Found through `form.elements` rather than under
   * this element: the payment block is rendered by the
   * shared layout, outside our scope, exactly like the
   * submit buttons.
   */
  pinnedPaymentChoice() {
    const form = this.orderForm();
    if (!form) return null;

    const field =
      form.elements[
        `${this.formScopeValue}[payment_choice]`
      ];
    // A RadioNodeList answers `.value` with whichever
    // radio is checked, so this stays correct if a
    // group ever appears alongside a hidden field.
    return field ? field.value : null;
  }

  /**
   * Builds a `{ key -> input }` map by querying the
   * controller scope for each address field by its
   * predictable Rails input name.
   */
  findAddressInputs() {
    const scope = this.formScopeValue;
    const form = this.orderForm();
    const map = {};
    ADDRESS_KEYS.forEach((key) => {
      const name =
        `${scope}[delivery_${key}]`;
      // Under our own element first, then anywhere in
      // the form. An address field can be rendered
      // outside this element and reconnected with the
      // `form` attribute, and `form.elements` is the
      // only lookup that sees one there.
      const input =
        this.element.querySelector(
          `[name="${name}"]`
        ) ||
        (form ? form.elements[name] : null);
      if (input) {
        map[key] = input;
      }
    });
    return map;
  }

  bindAddressListeners() {
    Object.values(this.addressInputs).forEach(
      (input) => {
        input.addEventListener(
          "input",
          this.boundAddressChanged
        );
        input.addEventListener(
          "change",
          this.boundAddressChanged
        );
      }
    );
  }

  unbindAddressListeners() {
    if (!this.addressInputs) return;
    Object.values(this.addressInputs).forEach(
      (input) => {
        input.removeEventListener(
          "input",
          this.boundAddressChanged
        );
        input.removeEventListener(
          "change",
          this.boundAddressChanged
        );
      }
    );
  }

  /**
   * Fired by the imperatively-bound input/change
   * listeners on each cached address input. Triggers
   * a debounced quote request after the user
   * finishes typing or changes a select.
   */
  addressChanged() {
    // Ahead of the debounce on purpose. The request is
    // worth waiting 600ms to batch; the customer's own
    // action is not. A button still reading "Add your
    // address to continue" half a second after they
    // added one reads as the page having missed it.
    if (this.collectAddress()) this.promptForPending();

    this.debouncedRequestQuote();
  }

  /**
   * Performs the actual POST to the quote endpoint.
   * Skips if the address payload is incomplete.
   */
  async requestQuote() {
    const payload = this.collectAddress();

    if (!payload) {
      // Incomplete, but by whose doing? See
      // `hasStreetAddress()`: an address the customer
      // gave whose city is mid-reload must not be met
      // with the copy for an address they never gave,
      // because that copy arrives by way of
      // `showNeedsAddress()`, which erases a refusal
      // and hides the way out of it (CAL-464).
      if (this.hasStreetAddress()) {
        this.holdQuote();
        return;
      }

      this.showNeedsAddress();
      return;
    }

    this.showLoading();
    const token = ++this.currentRequestToken;
    // Captured now, not read again on the way back:
    // the customer can toggle mid-flight, and the
    // hidden field has to record the terms THIS quote
    // was priced under.
    const paymentMethod = this.currentPaymentMethod();

    // One controller per attempt, held in a local rather
    // than on `this`: the timer below can only ever
    // abort the request it was created for, so a
    // deadline left over from an attempt the customer
    // has already superseded cannot reach through and
    // kill the one in flight now. The `finally` clears
    // it on every exit, the stale-response return
    // included.
    const abort = new AbortController();
    const deadline = window.setTimeout(
      () => abort.abort(),
      REQUEST_TIMEOUT_MS
    );

    try {
      const response = await post(this.urlValue, {
        body: {
          address: payload,
          payment_method: paymentMethod,
        },
        responseKind: "json",
        signal: abort.signal,
      });

      // Stale response: a newer request has
      // already been issued. Drop this one.
      if (token !== this.currentRequestToken) {
        return;
      }

      const data = await response.json;

      if (!response.ok) {
        const message =
          (data && data.error) ||
          this.unavailableTextValue;
        // The verdict travels with the sentence, and
        // both keys are the server's own snake_case:
        // this endpoint renders a plain Ruby hash, so
        // nothing transforms them on the way.
        this.showError(message, {
          retryable: data?.retryable === true,
          addressRelated: data?.address_related === true,
        });
        // A `cash_over_cap` refusal still carries a
        // verdict about cash, and it is the only thing
        // in the reply worth keeping: without it the
        // checkout would go on offering the option
        // that was just refused.
        this.applyCashAvailability(data);
        this.applyCardAvailability(data);
        return;
      }

      this.applyQuote(data, paymentMethod);
    } catch (error) {
      if (token !== this.currentRequestToken) {
        return;
      }
      console.error(
        "[courier-quote] request failed",
        error
      );
      // Our own deadline is not news. It is reported to
      // the customer like any other transient failure
      // (same sentence, same working "Try again"), but a
      // stalled connection on a phone is an ordinary
      // fact about the network rather than a defect this
      // app can act on, and one per stalled request
      // would drown the issues that are.
      if (!this.abortedByUs(error, abort)) {
        Sentry.captureException(error);
      }
      // Nothing answered, so nothing was refused: the
      // address may well be perfect. Retryable by
      // definition (a dropped connection is the one
      // failure asking again reliably clears), and said
      // in the words the server uses for its own
      // transient case. Falls back to the older sentence
      // only where the view supplies no value for this
      // one.
      //
      // A timeout lands here too, and wants exactly this:
      // the request failed without saying anything about
      // the address, and asking again is the move. No new
      // copy: every sentence this controller says is one
      // the server translated and handed over.
      this.showError(
        this.requestFailedTextValue ||
          this.unavailableTextValue,
        { retryable: true }
      );
    } finally {
      // Before any early return above could leave it
      // running. A deadline outliving its request would
      // abort nothing (the signal is spent) but would
      // hold a timer per quote for a minute and a half.
      window.clearTimeout(deadline);
    }
  }

  /**
   * Did OUR deadline end this request, rather than the
   * network? Read off the signal we own: an `AbortError`
   * alone would also match a `fetch` the browser
   * cancelled for its own reasons.
   */
  abortedByUs(error, abort) {
    return (
      abort.signal.aborted && error?.name === "AbortError"
    );
  }

  /**
   * The "Try again" button on the failure line.
   *
   * Straight to `requestQuote()`, NOT through the
   * debounce: the 600ms exists to batch keystrokes, and
   * a customer who just pressed a button has already
   * decided. The staleness token is incremented inside
   * `requestQuote()` like every other call site, so a
   * response still in flight from the failed attempt
   * cannot land on top of this one.
   *
   * Double-clicks need no guard of their own:
   * `requestQuote()` opens with `showLoading()`, which
   * puts the failure line and this button away before a
   * second press can reach it.
   */
  retryQuote() {
    this.requestQuote();
  }

  /**
   * Reads the cached address inputs and returns a
   * JSON-friendly payload, or null when the required
   * fields are not yet filled in.
   */
  collectAddress() {
    if (!this.addressInputs) return null;

    const payload = {};
    Object.entries(this.addressInputs).forEach(
      ([key, input]) => {
        const value = (input.value || "").trim();
        if (value !== "") {
          payload[key] = value;
        }
      }
    );

    // Belt and braces, matching the server-side form
    // default. The address sheet renders a hidden
    // `delivery_country` and `findAddressInputs` picks
    // it up, so this normally arrives filled and the
    // fallback never fires (CAL-445).
    //
    // It reads as a default rather than the source of
    // truth on purpose: the input was missing between
    // CAL-420 and CAL-445, and a comment saying so is
    // what would tell the next reader this page has no
    // country field, the belief that let the radius
    // validation go unsatisfiable for every hand-typed
    // address without anyone noticing.
    if (!payload.country) {
      payload.country = "MX";
    }

    const hasRequired = REQUIRED_KEYS.every(
      (k) => payload[k]
    );
    if (!hasRequired) return null;

    // The pin rides along under the names the endpoint
    // permits, so the fee describes a trip to the point
    // the customer confirmed rather than to whatever a
    // geocode makes of the address text.
    const pin = this.collectPin();
    if (pin) {
      payload.latitude = pin.latitude;
      payload.longitude = pin.longitude;
    }

    return payload;
  }

  /**
   * The hidden pin inputs, looked up on every read
   * rather than cached at connect. They belong to the
   * map card (a sibling of this element, rendered after
   * it), so they are reached through the form, like the
   * payment radios and the submit button. A
   * cached miss would be permanent, and there is no
   * listener to bind that would justify holding on to
   * them.
   */
  pinInputs() {
    const form = this.orderForm();
    if (!form) return {};

    const inputs = {};
    PIN_KEYS.forEach((key) => {
      const name =
        `${this.formScopeValue}` +
        `[delivery_pin_${key}]`;
      const input = form.querySelector(
        `[name="${name}"]`
      );
      if (input) {
        inputs[key] = input;
      }
    });
    return inputs;
  }

  /**
   * Both coordinates or neither. Half a pin is not a
   * position: the endpoint falls back to geocoding
   * unless it gets the pair, so sending one axis alone
   * would pin nothing and only look like it had.
   */
  collectPin() {
    const inputs = this.pinInputs();
    const latitude = (
      inputs.latitude?.value || ""
    ).trim();
    const longitude = (
      inputs.longitude?.value || ""
    ).trim();

    if (latitude === "" || longitude === "") {
      return null;
    }

    return {latitude, longitude};
  }

  applyQuote(quote, paymentMethod) {
    if (this.hasPaymentMethodTarget) {
      this.paymentMethodTarget.value =
        paymentMethod || "prepaid";
    }
    // Carried back verbatim: this is the server's
    // own account of what it just priced, and the
    // only part of the reply the create believes.
    if (this.hasQuoteTokenTarget) {
      this.quoteTokenTarget.value =
        quote.quote_token || "";
    }
    if (this.hasQuoteCustomerFeeCentsTarget) {
      this.quoteCustomerFeeCentsTarget.value =
        quote.customer_fee_cents == null
          ? ""
          : String(quote.customer_fee_cents);
    }
    if (this.hasQuoteIdTarget) {
      this.quoteIdTarget.value = quote.quote_id || "";
    }
    if (this.hasQuoteExpiresAtTarget) {
      this.quoteExpiresAtTarget.value =
        quote.expires_at || "";
    }
    if (this.hasQuoteFeeCentsTarget) {
      // Hidden field stores the raw provider quote
      // so the server-side verify-fee check compares
      // the provider's number to the provider's
      // number. The customer share lives in
      // `formatted_fee` for display.
      const rawProviderCents =
        quote.provider_fee_cents != null
          ? quote.provider_fee_cents
          : quote.fee_cents;
      this.quoteFeeCentsTarget.value =
        rawProviderCents == null
          ? ""
          : String(rawProviderCents);
    }
    if (this.hasQuoteFeeCurrencyTarget) {
      this.quoteFeeCurrencyTarget.value =
        quote.currency || "";
    }
    if (this.hasCommittedSurchargeCentsTarget) {
      this.committedSurchargeCentsTarget.value =
        quote.surcharge_cents == null
          ? ""
          : String(quote.surcharge_cents);
    }
    if (this.hasFeeDisplayTarget) {
      this.feeDisplayTarget.textContent =
        quote.formatted_fee || "";
    }
    if (this.hasEtaDisplayTarget) {
      this.etaDisplayTarget.textContent =
        this.etaSentence(quote);
    }
    this.publishPrice(
      quote.customer_fee_cents,
      quote.formatted_fee
    );

    this.applyAutoDispatchCallout(quote);
    this.applyCashAvailability(quote);
    this.applyCardAvailability(quote);

    this.hideLoading();
    this.clearError();
    this.hideNeedsAddress();
    this.showPanel();
    this.showQuoteSummary();
    this.scheduleRequote(quote.expires_at);
    this.refreshSubmitState();
    this.applySubmitPrompt(false);
  }

  /**
   * Renders or hides the "courier already on the way"
   * callout below the fee line. The server returns
   * `auto_dispatch_active: true` only when the venue
   * has the toggle on AND the cart has full prep_time
   * coverage, i.e., the order would in fact
   * auto-dispatch on confirm. Anything else hides the
   * callout (we don't promise speed we can't
   * deliver).
   */
  applyAutoDispatchCallout(quote) {
    if (!this.hasAutoDispatchCalloutTarget) return;

    const active =
      quote.auto_dispatch_active === true &&
      !!quote.formatted_dropoff_eta;

    if (!active) {
      this.autoDispatchCalloutTarget.classList.add(
        "hidden"
      );
      if (this.hasAutoDispatchEtaTarget) {
        this.autoDispatchEtaTarget.textContent = "";
      }
      return;
    }

    if (this.hasAutoDispatchEtaTarget) {
      this.autoDispatchEtaTarget.textContent =
        this.fillEta(
          this.autoDispatchTextValue,
          quote.formatted_dropoff_eta
        );
    }
    this.autoDispatchCalloutTarget.classList.remove(
      "hidden"
    );
  }

  /**
   * Turns the cash option on or off from the server's
   * verdict. The cap is charged against the at-door
   * total (the goods PLUS the customer's share of the
   * fee), so only a priced quote can answer it, and
   * the option ships enabled because at first render
   * there is no address, no fee and no total to weigh.
   *
   * Absent verdict means "not asked about" (a bad
   * address, an outage), not "unavailable": those
   * replies say nothing about cash and must not be
   * allowed to withdraw an option the customer may
   * still be entitled to.
   */
  applyCashAvailability(quote) {
    if (!quote) return;
    if (
      quote.cash_available === undefined ||
      quote.cash_available === null
    ) {
      return;
    }

    if (quote.cash_available) {
      this.enableCashOption();
    } else {
      this.disableCashOption(
        quote.cash_unavailable_reason
      );
    }
  }

  /**
   * The same verdict for the card rail (CAL-394). The
   * venue's own delivery balance pays the courier, and
   * a prepaid trip takes the WHOLE fee out of it. An
   * empty wallet withdraws paying online before it
   * withdraws anything else.
   *
   * Absent verdict means "not asked about", exactly as
   * on the cash side: a bad address or an outage says
   * nothing about the balance and must not withdraw an
   * option the customer is still entitled to.
   */
  applyCardAvailability(quote) {
    if (!quote) return;
    if (
      quote.card_available === undefined ||
      quote.card_available === null
    ) {
      return;
    }

    if (quote.card_available) {
      this.enableCardOption();
    } else {
      this.disableCardOption();
    }
  }

  enableCardOption() {
    const radio = this.payNowRadio();
    if (radio) radio.disabled = false;

    this.toggleCardOption(false);
  }

  /**
   * The card row's dimming and its explanation, which
   * used to be missing entirely: `disableCardOption`
   * disabled the radio and stopped there, so the
   * customer met a normal-looking, apparently
   * clickable "Pay now" row (card, Apple Pay or
   * Google Pay) that did nothing when tapped, with
   * nothing on the page saying why.
   *
   * The cash side has said "disabled, not hidden: the
   * customer needs to learn WHY" since it was written.
   * This is the same bar, drawn the same way, on the
   * rail an un-migrated venue's empty Listo balance
   * closes (CAL-394).
   */
  toggleCardOption(unavailable) {
    const option = this.cardOptionElement();
    if (!option) return;

    const label = option.querySelector("label");
    if (label) {
      label.classList.toggle("opacity-50", unavailable);
      label.classList.toggle(
        "pointer-events-none",
        unavailable
      );
    }

    const notice = option.querySelector(
      "[data-courier-quote-card-notice]"
    );
    if (notice) {
      notice.classList.toggle("hidden", !unavailable);
    }
  }

  cardOptionElement() {
    const form = this.orderForm();
    if (!form) return null;

    return form.querySelector(
      "[data-courier-quote-card-option]"
    );
  }

  /**
   * Order matters here for the same reason it does on
   * the cash side: a disabled radio is not submitted,
   * so leaving it checked would post an order carrying
   * no payment choice. The selection moves to cash
   * FIRST, and only if cash is actually available,
   * since a checkout with neither rail is one the
   * order type should never have offered.
   */
  disableCardOption() {
    const radio = this.payNowRadio();
    if (!radio) return;

    const later = this.payLaterRadio();
    const cashUsable = later && !later.disabled;

    if (radio.checked && cashUsable) {
      later.checked = true;
      later.dispatchEvent(
        new Event("change", { bubbles: true })
      );
    }

    // Dimmed only when it is actually withdrawn. With
    // no usable cash rail the card stays live (a
    // checkout with neither is one the order type
    // should never have offered), and dimming a row
    // the customer must still use would be worse than
    // saying nothing.
    if (cashUsable) {
      radio.disabled = true;
      this.toggleCardOption(true);
    }
  }

  payNowRadio() {
    return (this.paymentChoiceInputs || []).find(
      (input) => input.value === "now"
    );
  }

  enableCashOption() {
    const option = this.cashOptionElement();
    if (!option) return;

    const radio = this.payLaterRadio();
    if (radio) radio.disabled = false;

    const label = option.querySelector("label");
    if (label) {
      label.classList.remove(
        "opacity-50",
        "pointer-events-none"
      );
    }
    this.toggleCashNotice(option, false);
  }

  disableCashOption(reason) {
    const option = this.cashOptionElement();
    if (!option) return;

    const radio = this.payLaterRadio();
    if (radio) {
      // Order matters: move the selection BEFORE
      // disabling. A disabled radio is not submitted
      // at all, so leaving it checked would post an
      // order carrying no payment choice: a blank
      // where the customer thinks they answered.
      if (radio.checked) this.selectPayNow();
      radio.disabled = true;
    }

    const label = option.querySelector("label");
    if (label) {
      label.classList.add(
        "opacity-50",
        "pointer-events-none"
      );
    }
    this.toggleCashNotice(option, true, reason);
  }

  /**
   * Dispatched rather than assigned so `radio-dependent`
   * hears it and brings the tip UI back: the tip is
   * hidden on cash, and a silent flip back to paying
   * online would leave it hidden with no way to reach
   * it.
   */
  selectPayNow() {
    const now = (this.paymentChoiceInputs || []).find(
      (input) => input.value === "now"
    );
    if (!now) return;

    now.checked = true;
    now.dispatchEvent(
      new Event("change", { bubbles: true })
    );
  }

  payLaterRadio() {
    return (this.paymentChoiceInputs || []).find(
      (input) => input.value === "later"
    );
  }

  cashOptionElement() {
    const form = this.orderForm();
    if (!form) return null;

    return form.querySelector(
      "[data-courier-quote-cash-option]"
    );
  }

  /**
   * Reveals the ONE notice that matches the refusal
   * and hides the other (CAL-406). Both are rendered
   * server-side (the cap's copy names an amount, and
   * formatting money belongs where the venue's
   * currency is known), so here we only pick.
   *
   * `visible: false` hides both: cash coming back is
   * not a reason, and leaving either notice up under a
   * live option contradicts it.
   *
   * An unrecognised or absent reason falls back to the
   * cap. It is the refusal that existed before this
   * verdict did, so a payload from an older server,
   * or the 422 `cash_over_cap` reply whose whole
   * subject is the cap, lands on the right copy
   * without having to name it.
   */
  toggleCashNotice(option, visible, reason) {
    const capNotice = option.querySelector(
      "[data-courier-quote-cash-notice]"
    );
    const saldoNotice = option.querySelector(
      "[data-courier-quote-cash-saldo-notice]"
    );

    const showSaldo = visible && reason === "saldo";
    const showCap = visible && !showSaldo;

    if (capNotice) {
      capNotice.classList.toggle("hidden", !showCap);
    }
    if (saldoNotice) {
      saldoNotice.classList.toggle(
        "hidden",
        !showSaldo
      );
    }
  }

  /**
   * Hands the priced fee to whoever draws the money,
   * rather than drawing it here.
   *
   * `tip-selector` owns every rendering of the total
   * (the sticky footer's CTA, the summary panel, the
   * order-details sheet) for two reasons, and either
   * alone would settle it. It sits on the page root,
   * so it can see all of them, while this controller
   * is scoped to the fulfillment section and a
   * Stimulus target is only ever found among an
   * element's DESCENDANTS: reaching into the summary
   * from here is not a thing that can work, and the
   * `hasXTarget` guards turned that into silence
   * rather than an error. And it recomputes the total
   * on every tip change, so anything written from
   * here would be overwritten the moment a customer
   * tipped.
   *
   * `feeCents` is the CUSTOMER's share as an integer,
   * added to the cart total on the other side. Null
   * means there is no price (a refused quote, an
   * address not given yet) and takes the total back
   * to the placeholder rather than leaving a number
   * the order can no longer be placed at.
   */
  publishPrice(feeCents, formattedFee) {
    window.dispatchEvent(
      new CustomEvent("courier-quote:priced", {
        detail: {
          feeCents:
            feeCents == null ? null : Number(feeCents),
          formattedFee: formattedFee || null,
        },
      })
    );
  }

  /**
   * "Delivery in ~25 min": the view's translated
   * sentence with the wait in it.
   *
   * A DURATION, not a clock time: "how long" is the
   * question a customer deciding whether to order has,
   * and it stays true while they read it. A wall-clock
   * arrival answers a question they did not ask and
   * goes stale as they fill in the form.
   */
  etaSentence(quote) {
    return this.fillEta(
      this.etaTextValue,
      this.etaMinutes(quote)
    );
  }

  fillEta(template, eta) {
    if (eta === null || eta === "" || eta === undefined) {
      return "";
    }
    if (!template) return String(eta);
    return template
      .replace("%{minutes}", eta)
      .replace("%{eta}", eta);
  }

  /**
   * Minutes from now until the courier is expected,
   * from the drop-off the provider named. Falls back
   * to the pickup leg when it named no drop-off, which
   * is the only other number it gives us. Null when it
   * gave neither, or when the drop-off has already
   * passed: a negative wait is worse than no wait.
   */
  etaMinutes(quote) {
    if (quote.dropoff_eta) {
      const eta = new Date(quote.dropoff_eta);
      if (!isNaN(eta.getTime())) {
        const minutes = Math.round(
          (eta.getTime() - Date.now()) / 60000
        );
        if (minutes > 0) return minutes;
      }
    }
    if (quote.pickup_duration) {
      return quote.pickup_duration;
    }
    return null;
  }

  /**
   * The hint, the quote, the spinner and the failure
   * share one slot under the address row, so showing
   * any of them means putting the other three away.
   */
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
    }
    if (this.hasErrorDisplayTarget) {
      this.errorDisplayTarget.classList.add(
        "hidden"
      );
    }
    this.hideErrorActions();
    this.hideNeedsAddress();
    this.hideQuoteSummary();
    this.promptForPending();
  }

  /**
   * The button is held right through the change from
   * "no address" to "no price", so nothing else moves
   * on it. The sentence is the only thing that can
   * tell the customer their address landed and
   * something is now happening.
   */
  promptForPending() {
    this.applySubmitPrompt(
      true,
      this.submitPendingTextValue
    );
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
    }
  }

  /**
   * The failure line, and the ONE way out it earns.
   *
   * The verdict comes from the server and is not
   * inferred from the sentence: `retryable` means
   * repeating the identical request could still produce
   * a price (a provider blip, a rate limit, a dropped
   * connection), and `addressRelated` means this address
   * is what was refused. They are not opposites. An
   * address refusal and a rejected API key are both
   * non-retryable, and only one of them is something the
   * customer can act on, so a refusal that is neither
   * gets no button at all. Its copy already names the
   * only move ("pick another delivery option", "pay
   * online instead"), and a button contradicting that
   * sentence is worse than an empty line.
   *
   * The submit button stays HELD through all three:
   * there is no price in any of them, so there is
   * nothing to place an order at. Only a landed quote
   * frees it, which is what makes the retry honest
   * rather than a way to hurry past a failure.
   */
  showError(
    message,
    { retryable = false, addressRelated = false } = {}
  ) {
    this.clearQuoteFields();
    this.hideLoading();
    this.writeError(message);
    this.applyErrorActions({ retryable, addressRelated });
    this.clearQuoteText();
    this.hideAutoDispatchCallout();
    this.hideQuoteSummary();
    this.hideNeedsAddress();
    this.showPanel();
    this.refreshSubmitState();
    // Kept PROMPTING, where this used to hand the
    // button back its "Place order" and the placeholder:
    // dead, and claiming the order could be placed. The
    // hold is right: a refused quote means no price and
    // often no deliverable address, so there is nothing
    // to place. Saying nothing about it was not.
    this.applySubmitPrompt(
      true,
      this.submitUnavailableTextValue
    );
    this.publishPrice(null, null);
  }

  /**
   * The failure line leads with an icon, so the copy
   * goes into the text node beside it where one is
   * rendered. Older markup has the message directly on
   * the wrapper.
   */
  writeError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
    } else if (this.hasErrorDisplayTarget) {
      this.errorDisplayTarget.textContent = message;
    }
    if (this.hasErrorDisplayTarget) {
      this.errorDisplayTarget.classList.remove(
        "hidden"
      );
    }
  }

  /**
   * Shows at most one of the two buttons and hides the
   * other, every time: never a toggle, so no state can
   * arrive carrying the button the previous one earned.
   * Retry wins where both are somehow true: it is the
   * cheaper thing to be wrong about, since pressing it
   * costs one request and re-opens nothing.
   *
   * Guarded rather than assumed. A Stimulus target
   * resolves only among the controller element's
   * DESCENDANTS, and `hasXTarget` answers false in
   * silence, so markup that renders no buttons (a
   * checkout predating them) still draws every message.
   */
  applyErrorActions({ retryable, addressRelated }) {
    const showRetry = retryable === true;
    const showFix = !showRetry && addressRelated === true;

    if (this.hasRetryButtonTarget) {
      this.retryButtonTarget.classList.toggle(
        "hidden",
        !showRetry
      );
    }
    if (this.hasFixAddressLinkTarget) {
      this.fixAddressLinkTarget.classList.toggle(
        "hidden",
        !showFix
      );
    }
  }

  /**
   * Every state that leaves the failure behind has to
   * take both buttons with it. A "Try again" surviving
   * into a landed quote offers to throw away the price
   * on screen; one surviving into the loading state
   * invites a second request on top of the one running.
   */
  hideErrorActions() {
    this.applyErrorActions({
      retryable: false,
      addressRelated: false,
    });
  }

  /**
   * No address is not a failure: nothing has gone
   * wrong and there is nothing to retry, so it reads
   * as the hint it is rather than in the error's red.
   * Checkouts that render no hint line fall back to
   * the error slot, which is where this copy lived
   * before the line existed.
   */
  showNeedsAddress() {
    this.showHint(
      this.needsAddressTextValue,
      this.submitNeedsAddressTextValue
    );
  }

  /**
   * The same line, for an address missing only its
   * city.
   *
   * It names the city rather than asking again for the
   * address, because the city is what is missing:
   * `holdQuote()` is the only caller and it is reached
   * with a street in hand, `collectAddress()` defaults
   * the country, and those three are the whole of
   * `REQUIRED_KEYS`. So the field is knowable, and
   * naming it is the difference between a customer who
   * knows where to look and one told to supply an
   * address they can see they already gave.
   *
   * Falls back to the address copy where a checkout
   * supplies none of its own: the generic ask is worse
   * than this one and better than an empty line.
   */
  showNeedsCity() {
    this.showHint(
      this.needsCityTextValue ||
        this.needsAddressTextValue,
      this.submitNeedsCityTextValue ||
        this.submitNeedsAddressTextValue
    );
  }

  showHint(text, submitText) {
    this.clearQuoteFields();
    this.hideLoading();
    // Ahead of the branch below, because one arm of it
    // WRITES into the failure line rather than clearing
    // it: a checkout with no hint line carries this
    // copy there, and neither button belongs under it.
    this.hideErrorActions();

    if (this.hasNeedsAddressTarget) {
      this.writeHint(text);
      this.needsAddressTarget.classList.remove(
        "hidden"
      );
      this.clearError();
    } else {
      this.writeError(text);
    }

    this.clearQuoteText();
    this.hideAutoDispatchCallout();
    this.hideQuoteSummary();
    this.refreshSubmitState();
    // Named explicitly rather than left to whatever
    // the span happens to hold: by the time an address
    // is REMOVED the sentence there is the pending
    // one, and "Calculating delivery…" over a checkout
    // with nowhere to deliver to is a page that looks
    // busy while waiting for something nobody is going
    // to supply.
    this.applySubmitPrompt(true, submitText);
    this.publishPrice(null, null);
  }

  /**
   * The hint's sentence, in the text node beside its
   * clock: writing onto the line itself would erase
   * the glyph, the same way it would on the failure
   * line.
   *
   * Markup with no text node, or a checkout that hands
   * over no sentence, keeps what the server rendered:
   * this line ships with copy in it, so there is
   * something to keep, and blanking it would trade a
   * roughly-right sentence for none at all.
   */
  writeHint(text) {
    if (!this.hasHintMessageTarget) return;
    if (!text) return;

    this.hintMessageTarget.textContent = text;
  }

  hideNeedsAddress() {
    if (this.hasNeedsAddressTarget) {
      this.needsAddressTarget.classList.add("hidden");
    }
  }

  clearQuoteText() {
    if (this.hasFeeDisplayTarget) {
      this.feeDisplayTarget.textContent = "";
    }
    if (this.hasEtaDisplayTarget) {
      this.etaDisplayTarget.textContent = "";
    }
  }

  /**
   * Plural on purpose: the fee line and the small
   * print beneath it are two elements describing one
   * quote, and a quote that has gone away should take
   * both with it.
   */
  showQuoteSummary() {
    this.quoteSummaryTargets.forEach((el) => {
      el.classList.remove("hidden");
    });
  }

  hideQuoteSummary() {
    this.quoteSummaryTargets.forEach((el) => {
      el.classList.add("hidden");
    });
  }

  hideAutoDispatchCallout() {
    if (this.hasAutoDispatchCalloutTarget) {
      this.autoDispatchCalloutTarget.classList.add(
        "hidden"
      );
    }
    if (this.hasAutoDispatchEtaTarget) {
      this.autoDispatchEtaTarget.textContent = "";
    }
  }

  clearError() {
    this.hideErrorActions();
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "";
    } else if (this.hasErrorDisplayTarget) {
      this.errorDisplayTarget.textContent = "";
    }
    if (this.hasErrorDisplayTarget) {
      this.errorDisplayTarget.classList.add(
        "hidden"
      );
    }
  }

  clearQuoteFields() {
    if (this.hasQuoteIdTarget) {
      this.quoteIdTarget.value = "";
    }
    if (this.hasQuoteExpiresAtTarget) {
      this.quoteExpiresAtTarget.value = "";
    }
    if (this.hasQuoteFeeCentsTarget) {
      this.quoteFeeCentsTarget.value = "";
    }
    if (this.hasQuoteFeeCurrencyTarget) {
      this.quoteFeeCurrencyTarget.value = "";
    }
    // Cleared with the rest of the set: the
    // committed surcharge describes the quote we
    // just threw away, and a stale one left behind
    // is what the server's surcharge-drift check
    // would compare against.
    if (this.hasCommittedSurchargeCentsTarget) {
      this.committedSurchargeCentsTarget.value = "";
    }
    // The customer share described the quote we just
    // threw away, and the server weighs the at-door cap
    // with it. Left behind, it would let a stale number
    // vouch for a basket that has no quote at all.
    if (this.hasQuoteCustomerFeeCentsTarget) {
      this.quoteCustomerFeeCentsTarget.value = "";
    }
    // The whole point of clearing: this token vouches
    // for the quote we just threw away (its fee, its
    // terms, its drop-off point). Left behind, it would
    // let the create bind a price no longer on offer.
    // Blanked, the next submit has no signed quote and
    // the server prices the trip itself.
    if (this.hasQuoteTokenTarget) {
      this.quoteTokenTarget.value = "";
    }
    // Cleared to "" rather than to the radio's current
    // value on purpose: this field says what was
    // PRICED, and nothing is. Blanking it reads as
    // "prepaid" server-side, so a customer sitting on
    // the cash option drifts and is re-quoted instead
    // of binding a quote that no longer exists.
    if (this.hasPaymentMethodTarget) {
      this.paymentMethodTarget.value = "";
    }
  }

  showPanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("hidden");
    }
  }

  /**
   * Disables the form submit button when no quote
   * is currently in hand. Re-enables when a quote
   * is present and not expired.
   *
   * The submit button often lives outside the
   * controller's element (in the shared layout
   * footer), so we don't insist on a Stimulus
   * target. Fall back to the closest form's
   * submit button.
   */
  refreshSubmitState() {
    const submits = this.submitButtons();
    if (submits.length === 0) return;

    // Recorded as OUR reason rather than assigned, so
    // releasing it cannot free a button `tip-selector`
    // is still holding for its own: a basket under the
    // online-payment floor stays blocked when a quote
    // finally lands, instead of handing the customer a
    // live CTA the server would bounce back to the
    // cart.
    const blocked = !this.holdsUsableQuote();
    submits.forEach((submit) => {
      setSubmitBlock(submit, "quoteBlocked", blocked);
    });
  }

  /**
   * "Add your address to continue" vs "Place order"
   * and the amount. The server renders whichever is
   * true on arrival; from then on this owns it,
   * because adding an address never reloads the page
   * and the button must stop asking for one the moment
   * it has one.
   *
   * Shared with `address-autocomplete`, which says the
   * same thing on a flat-fee delivery where there is
   * no quote to say it.
   */
  applySubmitPrompt(prompting, text) {
    setSubmitPrompt(this.orderForm(), prompting, text);
  }

  /**
   * Every submit the checkout offers, because it now
   * offers more than one: the sticky footer, the
   * desktop summary panel, and the order-details
   * bottom sheet each render a "Place order" button.
   * Disabling only the first would leave the other two
   * live, which is the whole guard gone.
   *
   * Read off `form.elements` rather than queried under
   * the form, because those buttons are rendered
   * OUTSIDE it and reconnected with the `form`
   * attribute: `querySelector` is scoped to
   * descendants and cannot see them, while
   * `form.elements` is defined to include every
   * form-associated control wherever it sits.
   */
  submitButtons() {
    if (this.hasSubmitTarget) {
      return this.submitTargets;
    }
    return submitButtonsFor(this.orderForm());
  }

  /**
   * The checkout form. Several things this controller
   * drives (the submit button, the payment-choice
   * radios, the cash option) are rendered by the
   * shared layout OUTSIDE our element, so they are
   * reached through the form rather than through the
   * controller scope. Found via the hidden quote
   * fields we do own.
   */
  orderForm() {
    return (
      this.element.closest("form") ||
      (this.hasQuoteIdTarget
        ? this.quoteIdTarget.closest("form")
        : null)
    );
  }

  /**
   * Schedules an auto re-quote 2 minutes before the quote
   * expires, and never sooner than
   * `MIN_REQUOTE_DELAY_MS`. If `expires_at` is missing or
   * unparseable, no timer is set.
   *
   * An expiry already behind us no longer re-quotes on the
   * spot. Where that is a genuinely stale page rather than
   * a skewed clock, the customer has already been served:
   * `holdsUsableQuote()` reads an expired quote as no
   * quote, so `requestQuoteIfNeededOnConnect` asks for a
   * price straight away and this timer is only ever the
   * background refresh behind it.
   */
  scheduleRequote(expiresAt) {
    this.clearRequoteTimer();
    if (!expiresAt) return;

    const expiry = Date.parse(expiresAt);
    if (isNaN(expiry)) return;

    const fireAt = expiry - REQUOTE_LEAD_MS;
    const delay = Math.max(
      fireAt - Date.now(),
      MIN_REQUOTE_DELAY_MS
    );

    this.requoteTimer = window.setTimeout(() => {
      this.debouncedRequestQuote();
    }, delay);
  }

  scheduleRequoteFromExisting() {
    if (!this.hasQuoteExpiresAtTarget) return;
    const value = this.quoteExpiresAtTarget.value;
    if (value) this.scheduleRequote(value);
  }

  clearRequoteTimer() {
    if (this.requoteTimer) {
      window.clearTimeout(this.requoteTimer);
      this.requoteTimer = null;
    }
  }
}
